import {
  discardTeamChatDraft,
  fetchTeamChatDraft,
  markTeamChatRead,
  postToTeamChat,
  requestTeamChatDraft,
  useSDK,
  type TeamChatDraft,
} from '@littlebird/sdk';
import { useMutation, useQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import { invalidateProjectChats } from '@/features/projects/project-shared-content.query';
import { queryClient } from '@/query/client';

// A drafted reply takes a turn; while it runs the draft is polled, since its
// realtime nudge may come before the commit
const DRAFTING_POLL_MS = 2_000;

export const projectChatQueryKey = (projectId: string, chatId: string) =>
  ['project-chat', projectId, chatId] as const;

const teamChatDraftQueryKey = (projectId: string, chatId: string) =>
  ['team-chat-draft', projectId, chatId] as const;

export const invalidateTeamChat = (projectId: string, chatId: string) => {
  void queryClient.invalidateQueries({
    queryKey: projectChatQueryKey(projectId, chatId),
  });
  void queryClient.invalidateQueries({
    queryKey: teamChatDraftQueryKey(projectId, chatId),
  });
  invalidateProjectChats(projectId);
};

export function useTeamChatDraftQuery(
  projectId: string | null,
  chatId: string
) {
  const sdk = useSDK();
  return useQuery({
    queryKey: teamChatDraftQueryKey(projectId ?? '', chatId),
    queryFn: () => fetchTeamChatDraft(sdk, projectId ?? '', chatId),
    enabled: projectId !== null,
    refetchInterval: (query) =>
      query.state.data?.text === null ? DRAFTING_POLL_MS : false,
  });
}

const setDraft = (
  projectId: string,
  chatId: string,
  draft: TeamChatDraft | null
) => queryClient.setQueryData(teamChatDraftQueryKey(projectId, chatId), draft);

export function useRequestTeamChatDraftMutation(
  projectId: string,
  chatId: string
) {
  const sdk = useSDK();
  return useMutation({
    mutationFn: (instruction: string | null) =>
      requestTeamChatDraft(sdk, projectId, chatId, instruction),
    onSuccess: (draft) => setDraft(projectId, chatId, draft),
  });
}

export function useDiscardTeamChatDraftMutation(
  projectId: string,
  chatId: string
) {
  const sdk = useSDK();
  return useMutation({
    mutationFn: () => discardTeamChatDraft(sdk, projectId, chatId),
    onMutate: () => setDraft(projectId, chatId, null),
  });
}

export function usePostToTeamChatMutation(projectId: string, chatId: string) {
  const sdk = useSDK();
  return useMutation({
    mutationFn: (post: { text: string; mentionIds: number[] }) =>
      postToTeamChat(sdk, projectId, chatId, {
        ...post,
        clientPostId: uuidv4(),
      }),
    // A sent draft is gone for good
    onSuccess: () => setDraft(projectId, chatId, null),
  });
}

export function useMarkTeamChatReadMutation(projectId: string, chatId: string) {
  const sdk = useSDK();
  return useMutation({
    mutationFn: () => markTeamChatRead(sdk, projectId, chatId),
    onSuccess: () => invalidateProjectChats(projectId),
  });
}
